// Files people give us, and how we give them back.
//
// Pen test AD25996 (CyberLab, September 2026), "Unfiltered File Upload Functionality":
// a company card receipt named x.html was stored and then served back inline as
// text/html on the app's own origin, which is stored XSS against whoever opened it.
// The accounts inbox did the same for anything an outside sender emailed in. So:
//
//   - serving goes through fileHeaders(), which draws only passive types inline and
//     hands everything else over as application/octet-stream and an attachment;
//   - uploads go through uploadRefusal(), which refuses active content by extension
//     and by declared type, and uploadContentType() stores a type worked out from a
//     known extension rather than the one the client claimed.

// ── What kind of file is this really? ────────────────────────────────────────
//
// A PDF that arrives from a mail client as "application/octet-stream" (extremely
// common, some clients send it for every attachment) used to be served back under
// that type. A browser will not render octet-stream, so the viewer showed an empty
// panel and the browser quietly downloaded the file instead: no message, no reason,
// just a blank space and a file in the Downloads folder. It also switched off the
// Extract Invoice button, which tested the same field.
//
// The filename is the better evidence when the declared type is a generic
// placeholder, so that is what this trusts. A specific declared type always wins:
// this only fills in for the ones that say nothing. Nothing in the map is a type a
// browser would run: html and htm used to be, and were the pen test's way in.
export const GENERIC_CONTENT_TYPES = new Set([
  '', 'application/octet-stream', 'binary/octet-stream', 'application/binary',
  'application/x-download', 'application/download', 'application/force-download',
  'application/unknown', 'unknown/unknown', 'content/unknown',
]);

const EXTENSION_CONTENT_TYPES = {
  pdf: 'application/pdf',
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  webp: 'image/webp', heic: 'image/heic', heif: 'image/heif',
  bmp: 'image/bmp', tif: 'image/tiff', tiff: 'image/tiff',
  csv: 'text/csv', txt: 'text/plain', md: 'text/markdown', json: 'application/json',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  vsd: 'application/vnd.visio', vsdx: 'application/vnd.ms-visio.drawing', mpp: 'application/vnd.ms-project',
  eml: 'message/rfc822', msg: 'application/vnd.ms-outlook', zip: 'application/zip',
};

const extensionOf = (filename) => {
  const name = String(filename || '').toLowerCase().replace(/[.\s]+$/, '');
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot + 1);
};
const baseType = (type) => String(type || '').split(';')[0].trim().toLowerCase();

export function resolveAttachmentContentType(filename, storedType) {
  const declared = String(storedType || '').trim().toLowerCase();
  if (!GENERIC_CONTENT_TYPES.has(baseType(declared))) return declared;
  return EXTENSION_CONTENT_TYPES[extensionOf(filename)] || 'application/octet-stream';
}

// ── Giving a file back ───────────────────────────────────────────────────────

// Types a browser displays but cannot run. Anything else, however it was described,
// arrives as a download.
const INLINE_TYPES = new Set([
  'application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp',
  'image/heic', 'image/heif', 'text/plain',
]);

// For a browser that draws the file anyway: no script, no requests, no plugins. Not
// on a PDF, because Chrome refuses to render a PDF inside a CSP sandbox.
const SANDBOX_CSP = "sandbox; default-src 'none'; img-src 'self' data:; style-src 'unsafe-inline'; object-src 'none'";

// No RFC 5987 filename* anywhere in the codebase, so a plain ASCII name: anything that
// could end the quoted value or the header becomes an underscore.
const headerFilename = (name) => String(name || '').replace(/[^\w.\-() ]/g, '_').slice(0, 200) || 'file';

/**
 * The headers for serving a stored file: its type (resolved from the name when the
 * stored one is generic), how it is disposed, and the sandbox. `download` forces an
 * attachment even for a passive type.
 */
export function fileHeaders(filename, storedType, { download = false } = {}) {
  const type = baseType(resolveAttachmentContentType(filename, storedType));
  const passive = INLINE_TYPES.has(type);
  const headers = {
    'Content-Type': passive ? type : 'application/octet-stream',
    'Content-Disposition': `${passive && !download ? 'inline' : 'attachment'}; filename="${headerFilename(filename)}"`,
    'X-Content-Type-Options': 'nosniff',
  };
  if (type !== 'application/pdf') headers['Content-Security-Policy'] = SANDBOX_CSP;
  return headers;
}

// ── Taking a file in ─────────────────────────────────────────────────────────
//
// The second pass at pen test AD25996 (final report AD26341, finding AD2). The first
// refused a blocklist of active types, which let through whatever the list forgot
// (.phtml, .php5, .mht, .swf, .cpl, .pif, .chm, .iso, .one, macro-enabled Office
// files) and never looked inside a file, so a program renamed .pdf went straight in.
// Now every upload goes through acceptUpload():
//
//   - each screen names a kind, and a kind is an ALLOWLIST of extensions with a size
//     cap. The general store's kind is `document`;
//   - the bytes have to be what the extension says (magic numbers). An Office file
//     carrying macros is refused whatever it is called, a zip may not carry programs,
//     scripts or web pages, and a text file may not be binary or a web page;
//   - the type stored comes from the extension, and the original name is kept only as
//     sanitised metadata on a generated key.
//
// Nothing here scans for malware. That decision, and the controls that stand in for
// it, are written down in docs/SECURITY.md.

// Web pages, scripts and programs: what a browser or Windows runs when it is opened.
// Never on an allowlist, and refused inside a zip.
const ACTIVE_EXTENSIONS = new Set([
  'html', 'htm', 'xhtml', 'shtml', 'xht', 'mht', 'mhtml', 'svg', 'svgz', 'xml', 'xsl', 'xslt',
  'js', 'mjs', 'cjs', 'jsx', 'ts', 'tsx', 'jse', 'wsh', 'wsc', 'sct',
  'exe', 'dll', 'msi', 'msp', 'mst', 'bat', 'cmd', 'com', 'scr', 'pif', 'cpl', 'ocx', 'sys', 'drv',
  'ps1', 'psm1', 'psd1', 'vb', 'vbs', 'vbe', 'wsf', 'hta', 'chm', 'hlp', 'inf', 'reg', 'lnk', 'url', 'scf',
  'msc', 'gadget', 'application', 'appref-ms', 'appx', 'msix', 'appinstaller', 'settingcontent-ms',
  'jar', 'class', 'swf', 'sh', 'bash', 'zsh', 'ksh', 'csh', 'run', 'deb', 'rpm', 'apk', 'dmg', 'pkg',
  'php', 'php3', 'php4', 'php5', 'php7', 'php8', 'phtml', 'phar', 'pht', 'phps',
  'asp', 'aspx', 'ascx', 'ashx', 'asmx', 'jsp', 'jspx', 'cfm', 'cgi', 'pl', 'pm', 'py', 'pyc', 'pyw', 'rb',
  'iso', 'img', 'vhd', 'vhdx', 'one', 'onepkg', 'library-ms', 'search-ms', 'xll', 'iqy', 'slk',
]);
// Office formats that carry macros by design.
const MACRO_EXTENSIONS = new Set([
  'docm', 'dotm', 'xlsm', 'xltm', 'xlsb', 'xlam', 'xla', 'pptm', 'potm', 'ppsm', 'ppam', 'ppa', 'sldm', 'vsdm', 'vstm', 'vssm',
]);
const ACTIVE_TYPES = new Set([
  'text/html', 'text/xml', 'application/xml', 'text/xsl',
  'text/javascript', 'application/javascript', 'application/x-javascript',
  'text/ecmascript', 'application/ecmascript', 'application/typescript', 'text/vbscript',
  'application/x-msdownload', 'application/x-msdos-program', 'application/x-msi',
  'application/x-ms-installer', 'application/x-bat', 'application/x-sh', 'text/x-sh',
  'application/x-httpd-php', 'application/x-php', 'text/x-php', 'text/x-python',
  'application/java-archive', 'application/x-java-archive', 'application/hta',
  'application/x-ms-shortcut', 'application/x-shockwave-flash', 'multipart/related',
  'application/vnd.ms-word.document.macroenabled.12', 'application/vnd.ms-excel.sheet.macroenabled.12',
  'application/vnd.ms-powerpoint.presentation.macroenabled.12',
]);
const isActiveType = (type) => ACTIVE_TYPES.has(type) || type.endsWith('+xml'); // svg+xml, xhtml+xml

const IMAGES = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'heif'];
const OFFICE = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'vsd', 'vsdx', 'mpp'];

// What each screen takes, how big, and what it says when a file is not one of them.
// `document` is the general store (/api/uploads) and the accounts inbox. A kind with
// strictType also wants the declared type to agree with the name, or to say nothing.
export const UPLOAD_KINDS = {
  document: {
    extensions: ['pdf', ...IMAGES, 'bmp', 'tif', 'tiff', ...OFFICE, 'csv', 'txt', 'md', 'eml', 'msg', 'zip'], maxMb: 70,
    rule: 'Ops takes a PDF, an image, an Office document (Word, Excel, PowerPoint, Visio or Project), a CSV or text file, an email (.eml or .msg) or a zip',
  },
  receipt: { extensions: ['pdf', ...IMAGES], maxMb: 20, strictType: true, rule: 'A receipt has to be a photo (JPG, PNG, GIF, WebP or HEIC) or a PDF' },
  'survey-photo': { extensions: IMAGES, maxMb: 20, strictType: true, rule: 'A survey photo has to be a JPG, PNG, GIF, WebP or HEIC image' },
  'survey-import': { extensions: ['docx', 'xlsx'], maxMb: 20, strictType: true, rule: 'A survey to import has to be the Word form (.docx) or the desk survey workbook (.xlsx). Save an older .doc or .xls in the newer format first' },
  pdf: { extensions: ['pdf'], maxMb: 70, strictType: true, rule: 'The published version has to be a PDF' },
  word: { extensions: ['doc', 'docx'], maxMb: 70, strictType: true, rule: 'The Word source has to be a .doc or .docx file' },
  certificate: { extensions: ['pdf', ...IMAGES, 'doc', 'docx'], maxMb: 25, rule: 'A certificate has to be a PDF, a photo or a Word document' },
  'audit-report': { extensions: ['pdf', ...IMAGES, 'doc', 'docx'], maxMb: 50, rule: 'An audit report has to be a PDF, a photo or a Word document' },
  image: { extensions: ['png', 'jpg', 'jpeg', 'gif', 'webp'], maxMb: 5, strictType: true, rule: 'Only PNG, JPEG, GIF and WebP images can be placed in a document' },
  contract: { extensions: ['pdf'], maxMb: 25, rule: 'A contract document has to be a PDF' },
  invoice: { extensions: ['pdf', 'png', 'jpg', 'jpeg'], maxMb: 20, rule: 'An invoice has to be a PDF or a photo (JPG or PNG)' },
  // Not a file-store upload: see acceptProfilePhoto() at the end of this file.
  'profile-photo': { extensions: ['jpg', 'png', 'webp'], maxMb: 0.5, strictType: true, rule: 'A profile photo has to be a JPEG, PNG or WebP image' },
};

/** The accept= attribute for a kind's file picker, so people are steered before they upload. */
export const acceptAttribute = (kind = 'document') => UPLOAD_KINDS[kind].extensions.map(e => `.${e}`).join(',');

const shownName = (filename) => String(filename || '').replace(/[\u0000-\u001f\u007f]/g, '').slice(0, 120);

/**
 * A storage-safe version of a name: no folders, nothing unprintable, at most 200
 * characters, and the extension kept on the end when the stem has to be cut.
 */
export function safeUploadName(filename) {
  const clean = String(filename || 'file').replace(/[^a-zA-Z0-9._\- ()[\]]/g, '_').replace(/^\.+/, '_') || 'file';
  if (clean.length <= 200) return clean;
  const dot = clean.lastIndexOf('.');
  const ext = dot > 0 && clean.length - dot <= 12 ? clean.slice(dot) : '';
  return clean.slice(0, 200 - ext.length) + ext;
}

// ── Looking inside ──────────────────────────────────────────────────────────

const ascii = (s) => [...s].map(c => c.charCodeAt(0));
const at = (b, sig, offset = 0) => b.length >= offset + sig.length && sig.every((v, i) => b[offset + i] === v);

function indexOfBytes(b, needle, from = 0, to = b.length) {
  const first = needle[0];
  const last = Math.min(to, b.length) - needle.length;
  for (let i = from; i <= last; i++) {
    if (b[i] !== first) continue;
    let j = 1;
    while (j < needle.length && b[i + j] === needle[j]) j++;
    if (j === needle.length) return i;
  }
  return -1;
}

/**
 * The names in a zip's central directory, or null when it cannot be read as a zip.
 * Names are stored in the clear even when the contents are encrypted, so this sees
 * what a zip carries without unpacking it. Zip64 is followed; nested zips are not.
 */
export function zipEntryNames(b) {
  const u16 = (o) => b[o] | (b[o + 1] << 8);
  const u32 = (o) => (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16)) + b[o + 3] * 0x1000000;
  const u64 = (o) => u32(o) + u32(o + 4) * 0x100000000;
  let eocd = -1;
  for (let i = b.length - 22; i >= Math.max(0, b.length - 65557); i--) {
    if (b[i] === 0x50 && b[i + 1] === 0x4B && b[i + 2] === 0x05 && b[i + 3] === 0x06) { eocd = i; break; }
  }
  if (eocd < 0) return null;
  let count = u16(eocd + 10);
  let offset = u32(eocd + 16);
  if (count === 0xFFFF || offset === 0xFFFFFFFF) {
    const loc = eocd - 20;
    if (loc < 0 || u32(loc) !== 0x07064b50) return null;
    const z = u64(loc + 8);
    if (z + 56 > b.length || u32(z) !== 0x06064b50) return null;
    count = u64(z + 32);
    offset = u64(z + 48);
  }
  const names = [];
  const decoder = new TextDecoder();
  let p = offset;
  for (let n = 0; n < count; n++) {
    if (p + 46 > b.length || u32(p) !== 0x02014b50) return null;
    const nameLen = u16(p + 28);
    if (p + 46 + nameLen > b.length) return null;
    names.push(decoder.decode(b.subarray(p + 46, p + 46 + nameLen)));
    p += 46 + nameLen + u16(p + 30) + u16(p + 32);
  }
  return names;
}

// A VBA project in a legacy Office file is a storage called _VBA_PROJECT, and the
// directory holds its name in UTF-16.
const VBA_UTF16 = [...'_VBA_PROJECT'].flatMap(c => [c.charCodeAt(0), 0]);
const MARKUP_START = /^\s*<(?:!doctype\s+html|html|head|body|script|svg|iframe|\?xml)\b/i;

const SIGNATURES = {
  png: (b) => at(b, [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A]),
  jpg: (b) => at(b, [0xFF, 0xD8, 0xFF]),
  gif: (b) => at(b, ascii('GIF87a')) || at(b, ascii('GIF89a')),
  webp: (b) => at(b, ascii('RIFF')) && at(b, ascii('WEBP'), 8),
  heic: (b) => at(b, ascii('ftyp'), 4),
  bmp: (b) => at(b, ascii('BM')),
  tif: (b) => at(b, [0x49, 0x49, 0x2A, 0x00]) || at(b, [0x4D, 0x4D, 0x00, 0x2A]),
  // Acrobat reads a header anywhere in the first kilobyte, so that is where it is looked for.
  pdf: (b) => indexOfBytes(b, ascii('%PDF-'), 0, 1024) !== -1,
  zip: (b) => at(b, [0x50, 0x4B, 0x03, 0x04]) || at(b, [0x50, 0x4B, 0x05, 0x06]),
  ole: (b) => at(b, [0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1]),
};
const FAMILY = {
  pdf: 'pdf', png: 'png', jpg: 'jpg', jpeg: 'jpg', gif: 'gif', webp: 'webp', heic: 'heic', heif: 'heic',
  bmp: 'bmp', tif: 'tif', tiff: 'tif',
  docx: 'ooxml', xlsx: 'ooxml', pptx: 'ooxml', vsdx: 'ooxml', zip: 'zip',
  doc: 'ole', xls: 'ole', ppt: 'ole', vsd: 'ole', mpp: 'ole', msg: 'ole',
  csv: 'text', txt: 'text', md: 'text', eml: 'text',
};

const macroRefusal = (shown, ext) => `"${shown}" has not been uploaded: it contains macros, which can run code when it is`
  + ` opened. Save a copy without macros${ext.endsWith('x') ? '' : ` (as a .${ext}x file)`} and upload that.`;

/** Why these bytes are not a safe file of this extension, or null when they are. */
function contentRefusal(ext, b, shown) {
  const family = FAMILY[ext];
  const notWhatItSays = `"${shown}" has not been uploaded: it is named as a .${ext} file but its contents are not one.`
    + ' Save or export it again and upload that.';
  if (family === 'text') {
    const utf16 = at(b, [0xFF, 0xFE]) || at(b, [0xFE, 0xFF]);
    if (utf16) return null;
    if (b.indexOf(0) !== -1) return notWhatItSays;
    const head = new TextDecoder('latin1').decode(b.subarray(at(b, [0xEF, 0xBB, 0xBF]) ? 3 : 0, 512));
    if (ext !== 'eml' && MARKUP_START.test(head)) return `"${shown}" has not been uploaded: it is a web page named as a .${ext} file.`;
    return null;
  }
  if (family === 'ooxml' || family === 'zip') {
    if (!SIGNATURES.zip(b)) return notWhatItSays;
    const names = zipEntryNames(b);
    if (!names) return notWhatItSays;
    const lower = names.map(n => n.toLowerCase());
    if (family === 'ooxml') {
      if (!lower.includes('[content_types].xml')) return notWhatItSays;
      return lower.some(n => n.endsWith('vbaproject.bin')) ? macroRefusal(shown, ext) : null;
    }
    const bad = names.find(n => {
      const e = extensionOf(n.split('/').pop());
      return ACTIVE_EXTENSIONS.has(e) || MACRO_EXTENSIONS.has(e);
    });
    return bad
      ? `"${shown}" has not been uploaded: it contains "${shownName(bad.split('/').pop())}". Programs, scripts,`
        + ' web pages and Office files with macros are refused inside a zip as well.'
      : null;
  }
  if (family === 'ole') {
    if (!SIGNATURES.ole(b)) return notWhatItSays;
    // An Outlook message holds its attachments' bytes, so a macro in one of those is
    // not the message's own; only the Office formats are held to this.
    if (ext !== 'msg' && indexOfBytes(b, VBA_UTF16) !== -1) return macroRefusal(shown, ext);
    return null;
  }
  return SIGNATURES[family]?.(b) ? null : notWhatItSays;
}

// The extension a declared type stands for, for a mail attachment with no name to go by.
const TYPE_EXTENSIONS = Object.fromEntries(Object.entries(EXTENSION_CONTENT_TYPES).reverse().map(([e, t]) => [t, e]));

/**
 * Why this upload is refused by name and declared type alone, or null when it is
 * not: the extension must be on the kind's allowlist and the declared type must not
 * be one a browser would run. acceptUpload() also checks the bytes; this is the half
 * that can be asked before there are any.
 */
export function uploadRefusal(filename, declaredType, kind = 'document') {
  const spec = UPLOAD_KINDS[kind] || UPLOAD_KINDS.document;
  const ext = extensionOf(filename);
  const type = baseType(declaredType);
  const shown = shownName(filename);
  const typeFits = !spec.strictType || GENERIC_CONTENT_TYPES.has(type) || spec.extensions.some(e => EXTENSION_CONTENT_TYPES[e] === type);
  if (spec.extensions.includes(ext) && typeFits && !isActiveType(type)) return null;
  if (spec === UPLOAD_KINDS.document) {
    const why = MACRO_EXTENSIONS.has(ext)
      ? ' An Office file with macros is refused because the macros can run code when it is opened: save a copy as .docx, .xlsx or .pptx.'
      : ' Web pages, scripts and programs are refused because they can run code when opened.';
    return `"${shown}" cannot be uploaded. ${spec.rule}.${why}`;
  }
  return `${spec.rule}. "${shown}" is not, so it has not been uploaded.`;
}

const toBytes = (v) => v instanceof Uint8Array ? v
  : v instanceof ArrayBuffer ? new Uint8Array(v)
  : ArrayBuffer.isView(v) ? new Uint8Array(v.buffer, v.byteOffset, v.byteLength) : null;

const tooLarge = (name, mb) => `"${name}" is larger than ${mb} MB, which is the most this screen takes.`;

/**
 * The one way a file is taken in. Give it the name, the type the client declared,
 * the kind of screen, and either `data` (base64; a data: URL prefix is fine) or
 * `bytes`. Returns
 *   { ok: true, bytes, size, ext, kind, contentType, filename, originalName }
 * where `filename` is the sanitised name to put on the end of a generated key and
 * `contentType` comes from the extension; or
 *   { ok: false, status, error, filename, kind }
 * with a sentence that can be shown beside the control as it is.
 *
 * `inferExtension` is for mail attachments, which can arrive with no extension: a
 * declared type then stands in for it, and the bytes still have to match.
 */
export function acceptUpload({ filename, declaredType = '', data, bytes, kind = 'document', inferExtension = false } = {}) {
  const spec = UPLOAD_KINDS[kind];
  if (!spec) throw new Error(`Unknown upload kind: ${kind}`);
  const originalName = shownName(filename) || 'file';
  const refuse = (error, status = 400) => ({ ok: false, status, error, filename: originalName, kind });
  let name = String(filename || '');
  let ext = extensionOf(name);
  if (inferExtension && !spec.extensions.includes(ext) && !ACTIVE_EXTENSIONS.has(ext) && !MACRO_EXTENSIONS.has(ext)) {
    const inferred = TYPE_EXTENSIONS[baseType(declaredType)];
    if (inferred && spec.extensions.includes(inferred)) {
      name = `${name || 'attachment'}.${inferred}`;
      ext = inferred;
    }
  }
  const byName = uploadRefusal(name, declaredType, kind);
  if (byName) return refuse(byName);

  const cap = spec.maxMb * 1024 * 1024;
  let b = bytes != null ? toBytes(bytes) : null;
  if (!b) {
    if (typeof data !== 'string' || !data) return refuse('The file is missing from the request.');
    const b64 = /^data:/i.test(data) && data.includes(',') ? data.slice(data.indexOf(',') + 1) : data;
    if (Math.floor(b64.length * 3 / 4) > cap + 3) return refuse(tooLarge(originalName, spec.maxMb), 413);
    let bin;
    try { bin = atob(b64); } catch { return refuse('The file did not arrive as valid base64.'); }
    b = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) b[i] = bin.charCodeAt(i);
  }
  if (b.length > cap) return refuse(tooLarge(originalName, spec.maxMb), 413);
  if (b.length === 0) return refuse(`"${originalName}" is empty, so there is nothing to upload.`);
  const byContent = contentRefusal(ext, b, originalName);
  if (byContent) return refuse(byContent);
  return {
    ok: true, bytes: b, size: b.length, ext, kind,
    contentType: EXTENSION_CONTENT_TYPES[ext] || 'application/octet-stream',
    filename: safeUploadName(name), originalName,
  };
}

/** The type to store an upload under: from a known extension, never from the client. */
export function uploadContentType(filename) {
  return EXTENSION_CONTENT_TYPES[extensionOf(filename)] || 'application/octet-stream';
}

// ── A profile photo ─────────────────────────────────────────────────────────
//
// Pen test AD26341, finding AD2, left over: a profile photo is not an upload to the
// file store but a data URL kept on the person's row, and GET /people/photos hands
// every one of them to every signed-in person. PATCH /people/me and the People edit
// stored whatever string arrived, so a web page or a program could sit there under
// any type it liked. Now a photo has to be a base64 data URL of a JPEG, PNG or WebP,
// small enough to be an avatar, whose bytes really are that image.
//
// My Info draws the chosen picture onto a canvas of at most 256 by 256 pixels and
// saves it as a JPEG at quality 0.82, which comes to tens of kilobytes. The cap is
// set well above that so a busy photo is never refused, and well below anything
// that could be used to store a file.
export const PROFILE_PHOTO_MAX_BYTES = UPLOAD_KINDS['profile-photo'].maxMb * 1024 * 1024;
const PROFILE_PHOTO_TYPES = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' };
const PROFILE_PHOTO_URL = /^data:(image\/(?:jpeg|png|webp));base64,([A-Za-z0-9+/]+={0,2})$/i;

/**
 * Whether a value may be stored as a profile photo. Null, undefined or an empty
 * string clears the photo. Returns { ok: true, value } with the value to store
 * (null when clearing), or { ok: false, status, error } with a sentence to show.
 */
export function acceptProfilePhoto(value) {
  if (value == null || value === '') return { ok: true, value: null };
  const refuse = (error, status = 400) => ({ ok: false, status, error });
  const notAPhoto = 'That photo has not been saved: a profile photo has to be a JPEG, PNG or WebP image. Choose another picture.';
  if (typeof value !== 'string') return refuse(notAPhoto);
  // A base64 string is four characters for every three bytes, plus the prefix.
  if (value.length > Math.ceil(PROFILE_PHOTO_MAX_BYTES / 3) * 4 + 64) {
    return refuse(`That photo has not been saved: it is larger than ${PROFILE_PHOTO_MAX_BYTES / 1024} KB. Choose a smaller picture.`, 413);
  }
  const m = PROFILE_PHOTO_URL.exec(value);
  if (!m) return refuse(notAPhoto);
  const type = m[1].toLowerCase();
  const ext = PROFILE_PHOTO_TYPES[type];
  const checked = acceptUpload({ filename: `photo.${ext}`, declaredType: type, data: m[2], kind: 'profile-photo' });
  if (!checked.ok) {
    return checked.status === 413
      ? refuse(`That photo has not been saved: it is larger than ${PROFILE_PHOTO_MAX_BYTES / 1024} KB. Choose a smaller picture.`, 413)
      : refuse(notAPhoto);
  }
  return { ok: true, value: `data:${type};base64,${m[2]}` };
}
